import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {Menu, X, Calendar} from 'lucide-react'
import FixNowBadge from '@/components/FixNowBadge'
import Logo from '@/components/Logo'

const navItems = [
  { to: '/', label: 'Accueil' },
  { to: '/depannage-urgence', label: 'Urgence 24/7' },
  { to: '/entretien', label: 'Entretien' },
  { to: '/installations', label: 'Installations' },
  { to: '/zones-intervention', label: 'Zones' },
  { to: '/a-propos', label: 'À propos' },
  { to: '/contact', label: 'Contact' },
]

export default function Header() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur shadow-soft' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-3">
            <Link to="/" className="group transition-opacity hover:opacity-90">
              <Logo variant="horizontal" />
            </Link>
            <FixNowBadge variant="compact" className="hidden md:inline-flex" />
          </div>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    isActive
                      ? 'text-urgent-600 bg-urgent-50'
                      : 'text-navy-600 hover:text-navy-700 hover:bg-navy-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <a
              href="https://fixnow.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl gradient-urgent text-white font-bold text-sm shadow-orange-glow hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
              <Calendar className="w-4 h-4" strokeWidth={2.5} />
              Voir notre disponibilité sur FixNow
            </a>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-navy-700 hover:bg-navy-50"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-navy-100 py-3 space-y-1 animate-fade-up">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `block px-3 py-2.5 text-sm font-semibold rounded-lg ${
                    isActive ? 'text-urgent-600 bg-urgent-50' : 'text-navy-700 hover:bg-navy-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href="https://fixnow.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl gradient-urgent text-white font-bold text-sm"
            >
              <Calendar className="w-4 h-4" strokeWidth={2.5} />
              Voir notre disponibilité sur FixNow
            </a>
            <div className="pt-2 flex justify-center">
              <FixNowBadge variant="compact" />
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
